import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, ChevronDown, ChevronUp, Plus, Check, Edit2, Trash2, Save, X, RefreshCw } from 'lucide-react';
import api from '../services/api';

const steps = ['Upload Data', 'Select Columns', 'Configure Codelist', 'Review & Edit Codes', 'Custom Instructions', 'Review & Submit'];

// ── Shared primitives ────────────────────────────────────────────────────────

const inp = (extra = {}) => ({
  width: '100%', padding: '8px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8,
  fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#334155',
  background: '#fff', fontFamily: 'inherit', ...extra,
});
const btn = (bg = '#0f172a', color = '#fff', extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color,
  border: bg === 'transparent' ? '1.5px solid #e2e8f0' : 'none',
  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', ...extra,
});
const card = (extra = {}) => ({
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', ...extra,
});
const sectionLabel = {
  padding: '10px 18px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
  fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em',
};
const labelCss = { fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 5, display: 'block' };

function AlertBox({ type = 'info', children, onClose }) {
  const map = {
    info:    { bg: '#eff6ff', border: '#3b82f6', color: '#1e40af' },
    success: { bg: '#f0fdf4', border: '#10b981', color: '#065f46' },
    warning: { bg: '#fffbeb', border: '#f59e0b', color: '#78350f' },
    error:   { bg: '#fff1f2', border: '#f43f5e', color: '#9f1239' },
  };
  const s = map[type] || map.info;
  return (
    <div style={{ background: s.bg, borderLeft: `4px solid ${s.border}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: s.color, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: s.color, fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>}
    </div>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      <div
        onClick={() => !disabled && onChange({ target: { checked: !checked } })}
        style={{
          width: 38, height: 22, borderRadius: 11, background: checked ? '#0f172a' : '#cbd5e1',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 19 : 3, width: 16, height: 16,
          borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
        }} />
      </div>
    </label>
  );
}

function Modal({ open, onClose, title, subtitle, icon, children, actions }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 520, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', fontFamily: 'inherit' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '24px 24px 20px' }}>
          {icon && <div style={{ width: 44, height: 44, borderRadius: 12, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 2, lineHeight: 1, marginTop: 2 }}><X size={18} /></button>
        </div>
        <div style={{ borderTop: '1px solid #f1f5f9', padding: '20px 24px' }}>{children}</div>
        {actions && <div style={{ borderTop: '1px solid #f1f5f9', background: '#f8fafc', padding: '14px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
}

function Chip({ label, onDelete, color }) {
  const colors = { primary: { bg: '#eff6ff', c: '#1e40af' }, default: { bg: '#f1f5f9', c: '#475569' } };
  const s = colors[color] || colors.default;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.c }}>
      {label}
      {onDelete && <span onClick={onDelete} style={{ cursor: 'pointer', marginLeft: 2, lineHeight: 1 }}>×</span>}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function OECodingForm() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [jobName, setJobName] = useState('');
  const [jobDescription, setJobDescription] = useState('');

  const [dataFile, setDataFile] = useState(null);
  const [dataFileInfo, setDataFileInfo] = useState(null);
  const [selectedDataSheet, setSelectedDataSheet] = useState('');
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const [codelistFile, setCodelistFile] = useState(null);
  const [codelistFileInfo, setCodelistFileInfo] = useState(null);
  const [selectedCodelistSheet, setSelectedCodelistSheet] = useState('');
  const [selectedCodelistSheets, setSelectedCodelistSheets] = useState([]);
  const pendingCodelistSheets = useRef([]);
  const [codeGroups, setCodeGroups] = useState([]);
  const [useAIGenerated, setUseAIGenerated] = useState(false);
  const [columnGroupMapping, setColumnGroupMapping] = useState({});

  const [customInstructions, setCustomInstructions] = useState([]);
  const [newInstruction, setNewInstruction] = useState('');

  const [exclusionConfig, setExclusionConfig] = useState({
    code_98: { label: 'No Information / N/A', keywords: ['N/A', 'Not applicable', 'No experience', 'Nothing'], description: '' },
    code_99: { label: 'Vague / Irrelevant', keywords: ['Good', 'Nice', 'Ok', 'Fine'], description: '' },
  });

  const [enableTranslation, setEnableTranslation] = useState(false);
  const [autoCreateCodes, setAutoCreateCodes] = useState(false);
  const [newCodeMinOccurrences, setNewCodeMinOccurrences] = useState(1);
  const [includeNets, setIncludeNets] = useState(true);
  const [includeSuperNets, setIncludeSuperNets] = useState(true);

  const [code98KeywordsText, setCode98KeywordsText] = useState('N/A, Not applicable, No experience, Nothing');
  const [code99KeywordsText, setCode99KeywordsText] = useState('Good, Nice, Ok, Fine');

  const [codelistConfirmed, setCodelistConfirmed] = useState(false);
  const [editableCodeGroups, setEditableCodeGroups] = useState([]);
  const [selectedGroupTab, setSelectedGroupTab] = useState(0);

  const [columnCodelistMapping, setColumnCodelistMapping] = useState({});

  const [referenceText, setReferenceText] = useState('');
  const [parsedReferenceExamples, setParsedReferenceExamples] = useState([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editCodeForm, setEditCodeForm] = useState({ code: '', codelist: '', nets: '', super_nets: '' });

  const DRAFT_KEY = 'oe_coding_draft';
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) setPendingDraft(JSON.parse(raw));
    } catch (_) {}
  }, []);

  const saveDraft = () => {
    const draft = {
      savedAt: new Date().toISOString(), activeStep, jobName, jobDescription,
      selectedDataSheet, dataFileInfo, availableColumns, selectedColumns,
      codelistFileInfo, selectedCodelistSheets, selectedCodelistSheet, useAIGenerated,
      columnCodelistMapping, editableCodeGroups, customInstructions, enableTranslation,
      autoCreateCodes, newCodeMinOccurrences, includeNets, includeSuperNets,
      code98KeywordsText, code99KeywordsText, parsedReferenceExamples,
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2500);
  };

  const restoreDraft = (d) => {
    setJobName(d.jobName || ''); setJobDescription(d.jobDescription || '');
    setSelectedDataSheet(d.selectedDataSheet || ''); setDataFileInfo(d.dataFileInfo || null);
    setAvailableColumns(d.availableColumns || []); setSelectedColumns(d.selectedColumns || []);
    setCodelistFileInfo(d.codelistFileInfo || null); setSelectedCodelistSheets(d.selectedCodelistSheets || []);
    setSelectedCodelistSheet(d.selectedCodelistSheet || '');
    pendingCodelistSheets.current = d.selectedCodelistSheets || [];
    setUseAIGenerated(d.useAIGenerated || false); setColumnCodelistMapping(d.columnCodelistMapping || {});
    setEditableCodeGroups(d.editableCodeGroups || []); setCustomInstructions(d.customInstructions || []);
    setEnableTranslation(d.enableTranslation || false); setAutoCreateCodes(d.autoCreateCodes || false);
    setNewCodeMinOccurrences(d.newCodeMinOccurrences || 1);
    setIncludeNets(d.includeNets !== undefined ? d.includeNets : true);
    setIncludeSuperNets(d.includeSuperNets !== undefined ? d.includeSuperNets : true);
    setCode98KeywordsText(d.code98KeywordsText || ''); setCode99KeywordsText(d.code99KeywordsText || '');
    setParsedReferenceExamples(d.parsedReferenceExamples || []);
    setActiveStep(d.activeStep || 0); setPendingDraft(null);
  };

  const discardDraft = () => { localStorage.removeItem(DRAFT_KEY); setPendingDraft(null); };

  const handleDataFileUpload = async (event) => {
    const file = event.target.files[0]; if (!file) return;
    setDataFile(file); setIsLoading(true); setError(null);
    const formData = new FormData(); formData.append('file', file);
    try {
      const response = await api.post('oe_coding/jobs/upload-file/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDataFileInfo(response.data);
      if (response.data.sheets.length === 1) {
        setSelectedDataSheet(response.data.sheets[0]);
        setAvailableColumns(response.data.columns[response.data.sheets[0]] || []);
      }
    } catch (err) { setError('Failed to upload file: ' + (err.response?.data?.error || err.message)); }
    finally { setIsLoading(false); }
  };

  const handleDataSheetChange = (sheetName) => {
    setSelectedDataSheet(sheetName);
    setAvailableColumns(dataFileInfo?.columns[sheetName] || []);
    setSelectedColumns([]);
  };

  const handleCodelistFileUpload = async (event) => {
    const file = event.target.files[0]; if (!file) return;
    setCodelistFile(file); setIsLoading(true); setError(null);
    const formData = new FormData(); formData.append('file', file);
    try {
      const response = await api.post('oe_coding/jobs/upload-file/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCodelistFileInfo(response.data);
      if (response.data.sheets.length === 1) {
        setSelectedCodelistSheets([response.data.sheets[0]]);
        setSelectedCodelistSheet(response.data.sheets[0]);
        await parseCodelist(response.data.file_path, response.data.sheets[0]);
      } else { setSelectedCodelistSheets([]); }
    } catch (err) { setError('Failed to upload codelist: ' + (err.response?.data?.error || err.message)); }
    finally { setIsLoading(false); }
  };

  const parseReferenceText = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    const examples = [];
    for (const line of lines) {
      const arrowIdx = line.lastIndexOf('->'); if (arrowIdx === -1) continue;
      const responseText = line.substring(0, arrowIdx).trim();
      const labelsStr = line.substring(arrowIdx + 2).trim();
      const labelMatches = labelsStr.match(/"([^"]+)"/g);
      const labels = labelMatches ? labelMatches.map(l => l.replace(/"/g, '').trim()).filter(l => l)
        : labelsStr.split(',').map(l => l.trim().replace(/^"|"$/g, '')).filter(l => l);
      if (responseText && labels.length > 0) examples.push({ text: responseText, labels });
    }
    return examples;
  };

  const handleReferenceTextChange = (text) => {
    setReferenceText(text); setParsedReferenceExamples(parseReferenceText(text));
  };

  const parseAllCodelistSheets = async (filePath, sheets) => {
    try {
      const allGroups = [];
      for (const sheet of sheets) {
        const response = await api.post('oe_coding/jobs/parse-codelist/', { file_path: filePath, sheet_name: sheet });
        const groups = response.data.code_groups || [];
        groups.forEach(g => { if (!g.header || g.header === 'Codelist') g.header = sheet; });
        allGroups.push(...groups);
      }
      setCodeGroups(allGroups); setEditableCodeGroups(JSON.parse(JSON.stringify(allGroups)));
      setCodelistConfirmed(false); setSelectedCodelistSheet(sheets.join(', '));
      const mapping = {};
      selectedColumns.forEach(col => {
        for (let i = 0; i < allGroups.length; i++) {
          const group = allGroups[i];
          const header = (group.header || '').toLowerCase();
          const targetCols = (group.target_columns || []).map(c => c.toLowerCase());
          if (targetCols.includes(col.toLowerCase()) || header.includes(col.toLowerCase()) || col.toLowerCase().includes(header)) { mapping[col] = i; break; }
        }
      });
      setColumnCodelistMapping(mapping);
    } catch (err) { setError('Failed to parse codelist sheets: ' + (err.response?.data?.error || err.message)); }
  };

  const parseCodelist = async (filePath, sheetName) => {
    try {
      const response = await api.post('oe_coding/jobs/parse-codelist/', { file_path: filePath, sheet_name: sheetName });
      const groups = response.data.code_groups || [];
      setCodeGroups(groups); setEditableCodeGroups(JSON.parse(JSON.stringify(groups))); setCodelistConfirmed(false);
      if (groups.length === 1 && selectedColumns.length > 0) {
        const mapping = {}; selectedColumns.forEach(col => { mapping[col] = 0; }); setColumnCodelistMapping(mapping);
      } else if (groups.length > 1) {
        const mapping = {};
        selectedColumns.forEach(col => {
          for (let i = 0; i < groups.length; i++) {
            const group = groups[i];
            const header = (group.header || '').toLowerCase();
            const targetCols = (group.target_columns || []).map(c => c.toLowerCase());
            if (targetCols.includes(col.toLowerCase()) || header === col.toLowerCase()) { mapping[col] = i; break; }
          }
        });
        setColumnCodelistMapping(mapping);
      } else { setColumnCodelistMapping({}); }
    } catch (err) { setError('Failed to parse codelist: ' + (err.response?.data?.error || err.message)); }
  };

  const handleEditCode = (groupIndex, codeIndex) => {
    const code = editableCodeGroups[groupIndex].codes[codeIndex];
    setEditingCode({ groupIndex, codeIndex });
    setEditCodeForm({ code: code.code?.toString() || '', codelist: code.codelist || '', nets: code.nets || '', super_nets: code.super_nets || '' });
    setEditDialogOpen(true);
  };

  const handleAddCode = (groupIndex) => {
    const group = editableCodeGroups[groupIndex];
    const maxCode = Math.max(...group.codes.map(c => c.code || 0), 0);
    setEditingCode({ groupIndex, codeIndex: -1 });
    setEditCodeForm({ code: (maxCode + 1).toString(), codelist: '', nets: '', super_nets: '' });
    setEditDialogOpen(true);
  };

  const handleSaveCode = () => {
    const { groupIndex, codeIndex } = editingCode;
    const newGroups = [...editableCodeGroups];
    const newCode = { code: parseInt(editCodeForm.code) || 0, codelist: editCodeForm.codelist, nets: editCodeForm.nets || null, super_nets: editCodeForm.super_nets || null };
    if (codeIndex === -1) { newGroups[groupIndex].codes.push(newCode); newGroups[groupIndex].codes_count = newGroups[groupIndex].codes.length; }
    else { newGroups[groupIndex].codes[codeIndex] = newCode; }
    newGroups[groupIndex].codes.sort((a, b) => (a.code || 0) - (b.code || 0));
    setEditableCodeGroups(newGroups); setEditDialogOpen(false); setEditingCode(null); setCodelistConfirmed(false);
  };

  const handleDeleteCode = (groupIndex, codeIndex) => {
    if (!window.confirm('Are you sure you want to delete this code?')) return;
    const newGroups = [...editableCodeGroups];
    newGroups[groupIndex].codes.splice(codeIndex, 1);
    newGroups[groupIndex].codes_count = newGroups[groupIndex].codes.length;
    setEditableCodeGroups(newGroups); setCodelistConfirmed(false);
  };

  const handleConfirmCodelist = () => {
    const unassigned = getUnassignedColumns();
    if (unassigned.length > 0 && editableCodeGroups.length > 0) {
      if (!window.confirm(`The following columns are not assigned to any codelist: ${unassigned.join(', ')}. Continue anyway? (They will use the first code group)`)) return;
      const mapping = { ...columnCodelistMapping };
      unassigned.forEach(col => { mapping[col] = 0; });
      setColumnCodelistMapping(mapping);
    }
    setCodelistConfirmed(true);
    const groupsWithColumns = editableCodeGroups.map((group, idx) => ({ ...group, target_columns: getColumnsForGroup(idx) }));
    setCodeGroups(groupsWithColumns);
  };

  const handleUpdateGroupDescription = (groupIndex, description) => {
    setEditableCodeGroups(prev => { const updated = [...prev]; updated[groupIndex] = { ...updated[groupIndex], description }; return updated; });
    setCodelistConfirmed(false);
  };

  const handleAddNewCodeGroup = (groupName = 'New Code Group') => {
    const newGroup = { header: groupName, description: '', target_columns: [], codes: [], codes_count: 0 };
    setEditableCodeGroups(prev => [...prev, newGroup]); setCodelistConfirmed(false);
    setSelectedGroupTab(editableCodeGroups.length);
  };

  const handleAssignColumnToGroup = (columnName, groupIndex) => {
    setColumnCodelistMapping(prev => ({ ...prev, [columnName]: groupIndex })); setCodelistConfirmed(false);
  };

  const handleRemoveColumnFromGroup = (columnName) => {
    setColumnCodelistMapping(prev => { const updated = { ...prev }; delete updated[columnName]; return updated; }); setCodelistConfirmed(false);
  };

  const handleRenameCodeGroup = (groupIndex, newName) => {
    setEditableCodeGroups(prev => { const updated = [...prev]; updated[groupIndex] = { ...updated[groupIndex], header: newName }; return updated; });
    setCodelistConfirmed(false);
  };

  const handleDeleteCodeGroup = (groupIndex) => {
    if (!window.confirm('Are you sure you want to delete this code group? All codes in this group will be removed.')) return;
    setEditableCodeGroups(prev => prev.filter((_, idx) => idx !== groupIndex));
    setColumnCodelistMapping(prev => {
      const updated = {};
      Object.entries(prev).forEach(([col, idx]) => {
        if (idx < groupIndex) updated[col] = idx;
        else if (idx > groupIndex) updated[col] = idx - 1;
      });
      return updated;
    });
    if (selectedGroupTab >= groupIndex && selectedGroupTab > 0) setSelectedGroupTab(prev => prev - 1);
    setCodelistConfirmed(false);
  };

  const getColumnsForGroup = (groupIndex) => Object.entries(columnCodelistMapping).filter(([_, idx]) => idx === groupIndex).map(([col]) => col);
  const getUnassignedColumns = () => selectedColumns.filter(col => !(col in columnCodelistMapping));

  const handleCodelistSheetChange = async (sheetName) => {
    setSelectedCodelistSheet(sheetName);
    if (codelistFileInfo) await parseCodelist(codelistFileInfo.file_path, sheetName);
  };

  const handleCodelistSheetsChange = async (selectedSheets) => {
    if (codelistFileInfo && selectedSheets.length > 0) {
      setIsLoading(true);
      if (selectedSheets.length === 1) { setSelectedCodelistSheet(selectedSheets[0]); await parseCodelist(codelistFileInfo.file_path, selectedSheets[0]); }
      else { await parseAllCodelistSheets(codelistFileInfo.file_path, selectedSheets); }
      setIsLoading(false);
    }
  };

  const handleColumnToggle = (column) => {
    setSelectedColumns(prev => prev.includes(column) ? prev.filter(c => c !== column) : [...prev, column]);
  };

  const handleAddInstruction = () => {
    if (newInstruction.trim()) { setCustomInstructions(prev => [...prev, newInstruction.trim()]); setNewInstruction(''); }
  };

  const handleRemoveInstruction = (index) => setCustomInstructions(prev => prev.filter((_, i) => i !== index));
  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async () => {
    setIsLoading(true); setError(null);
    const finalExclusionConfig = {
      code_98: { ...exclusionConfig.code_98, keywords: code98KeywordsText.split(',').map(k => k.trim()).filter(k => k) },
      code_99: { ...exclusionConfig.code_99, keywords: code99KeywordsText.split(',').map(k => k.trim()).filter(k => k) },
    };
    try {
      const formData = new FormData();
      formData.append('name', jobName); formData.append('description', jobDescription);
      formData.append('data_file', dataFile); formData.append('data_file_name', dataFile.name);
      formData.append('data_sheet_name', selectedDataSheet);
      formData.append('target_columns', JSON.stringify(selectedColumns));
      formData.append('custom_instructions', JSON.stringify(customInstructions));
      formData.append('exclusion_config', JSON.stringify(finalExclusionConfig));
      formData.append('enable_translation', enableTranslation);
      formData.append('auto_create_codes', autoCreateCodes);
      formData.append('include_nets', includeNets);
      formData.append('include_super_nets', includeSuperNets);
      formData.append('new_code_min_occurrences', newCodeMinOccurrences);
      if (parsedReferenceExamples.length > 0) formData.append('reference_examples', JSON.stringify({ manual_examples: parsedReferenceExamples }));
      if (codelistFile && !useAIGenerated) {
        formData.append('codelist_file', codelistFile); formData.append('codelist_file_name', codelistFile.name);
        formData.append('codelist_sheet_name', selectedCodelistSheet);
      }
      if (editableCodeGroups.length > 0) {
        const columnGroupsData = editableCodeGroups.map((cg, idx) => {
          const assignedColumns = Object.entries(columnCodelistMapping).filter(([_, groupIdx]) => groupIdx === idx).map(([col]) => col);
          return { ...cg, target_columns: assignedColumns.length > 0 ? assignedColumns : cg.target_columns, ai_generated: useAIGenerated };
        });
        formData.append('column_groups', JSON.stringify(columnGroupsData));
      } else if (useAIGenerated) {
        formData.append('column_groups', JSON.stringify([{ header: 'AI Generated', target_columns: selectedColumns, codes: [], ai_generated: true }]));
      }
      const response = await api.post('oe_coding/jobs/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      localStorage.removeItem(DRAFT_KEY);
      navigate(`/oe-coding/${response.data.id}`);
    } catch (err) { setError('Failed to create job: ' + (err.response?.data?.error || err.message)); }
    finally { setIsLoading(false); }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0: return dataFile && selectedDataSheet && jobName;
      case 1: return selectedColumns.length > 0;
      case 2: return useAIGenerated || codeGroups.length > 0;
      case 3: return useAIGenerated || codelistConfirmed;
      case 4: return true;
      case 5: return true;
      default: return false;
    }
  };

  // ── Step renderers ───────────────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelCss}>Job Name <span style={{ color: '#f43f5e' }}>*</span></label>
            <input style={inp()} value={jobName} onChange={e => setJobName(e.target.value)} placeholder="e.g. Brand Awareness Q3 2026" />
          </div>
          <div>
            <label style={labelCss}>Description (Optional)</label>
            <textarea style={inp({ minHeight: 70, resize: 'vertical' })} value={jobDescription} onChange={e => setJobDescription(e.target.value)} placeholder="Brief description of this coding job" />
          </div>
          <div>
            <label style={labelCss}>Upload Data File <span style={{ color: '#f43f5e' }}>*</span></label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', border: '1.5px dashed #cbd5e1', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#475569', background: '#f8fafc' }}>
              <Upload size={14} /> {dataFile ? dataFile.name : 'Select Excel File'}
              <input type="file" hidden accept=".xlsx,.xls" onChange={handleDataFileUpload} />
            </label>
          </div>
          {dataFileInfo && (
            <div>
              <label style={labelCss}>Select Data Sheet</label>
              <select style={inp({ padding: '8px 12px' })} value={selectedDataSheet} onChange={e => handleDataSheetChange(e.target.value)}>
                <option value="">— select sheet —</option>
                {dataFileInfo.sheets.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
        </div>
      );

      case 1: return (
        <div>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Select the columns containing open-ended responses to code.</p>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 360, overflow: 'auto' }}>
            {availableColumns.map(column => (
              <label key={column} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: selectedColumns.includes(column) ? '#f0fdf4' : '#fff' }}>
                <input type="checkbox" checked={selectedColumns.includes(column)} onChange={() => handleColumnToggle(column)} style={{ accentColor: '#0f172a' }} />
                <span style={{ fontSize: 13, color: '#334155' }}>{column}</span>
              </label>
            ))}
          </div>
          {selectedColumns.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>Selected: {selectedColumns.length} column(s)</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {selectedColumns.map(col => <Chip key={col} label={col} onDelete={() => handleColumnToggle(col)} color="primary" />)}
              </div>
            </div>
          )}
        </div>
      );

      case 2: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <Toggle checked={useAIGenerated} onChange={e => {
              setUseAIGenerated(e.target.checked);
              if (e.target.checked && editableCodeGroups.length === 0) {
                const defaultGroup = { header: 'AI Generated Codes', target_columns: selectedColumns, codes: [], codes_count: 0 };
                setEditableCodeGroups([defaultGroup]);
                const mapping = {}; selectedColumns.forEach(col => { mapping[col] = 0; }); setColumnCodelistMapping(mapping);
              }
            }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Let AI generate codes from data</span>
          </label>

          {useAIGenerated && (
            <AlertBox type="info">AI will analyze your responses and automatically generate appropriate codes/themes.</AlertBox>
          )}

          {!useAIGenerated && (
            <>
              <div>
                <label style={labelCss}>Upload Codelist File</label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', border: '1.5px dashed #cbd5e1', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#475569', background: '#f8fafc' }}>
                  <Upload size={14} /> {codelistFile ? codelistFile.name : 'Upload Codelist File'}
                  <input type="file" hidden accept=".xlsx,.xls" onChange={handleCodelistFileUpload} />
                </label>
              </div>

              {codelistFileInfo && (
                <div>
                  <label style={labelCss}>Select Codelist Sheet(s)</label>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 200, overflow: 'auto' }}>
                    {codelistFileInfo.sheets.map(sheet => (
                      <label key={sheet} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: selectedCodelistSheets.includes(sheet) ? '#eff6ff' : '#fff' }}>
                        <input type="checkbox" checked={selectedCodelistSheets.includes(sheet)}
                          onChange={e => {
                            const next = e.target.checked ? [...selectedCodelistSheets, sheet] : selectedCodelistSheets.filter(s => s !== sheet);
                            setSelectedCodelistSheets(next); pendingCodelistSheets.current = next;
                            if (!e.target.checked && next.length > 0) handleCodelistSheetsChange(next);
                          }}
                          onBlur={() => { if (pendingCodelistSheets.current.length > 0) handleCodelistSheetsChange(pendingCodelistSheets.current); }}
                          style={{ accentColor: '#0f172a' }} />
                        <span style={{ fontSize: 13 }}>{sheet}</span>
                      </label>
                    ))}
                  </div>
                  {selectedCodelistSheets.length > 0 && (
                    <button style={btn('#3b82f6', '#fff', { marginTop: 10, fontSize: 12, padding: '6px 14px' })} onClick={() => handleCodelistSheetsChange(selectedCodelistSheets)}>
                      Load Selected Sheet(s)
                    </button>
                  )}
                </div>
              )}

              {codeGroups.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>Detected Code Groups ({codeGroups.length})</div>
                  {codeGroups.map((group, idx) => (
                    <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6 }}>
                      <div style={{ padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 13, fontWeight: 600, color: '#334155' }}>
                        {group.header} ({group.codes_count} codes)
                      </div>
                      <div style={{ padding: '8px 14px', fontSize: 12, color: '#64748b' }}>
                        Sample: {group.codes.slice(0, 5).map(c => c.codelist).join(', ')}{group.codes.length > 5 && '…'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      );

      case 3:
        if (useAIGenerated) return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AlertBox type="info">AI will generate codes automatically. Configure separate code groups below if different columns need separate codelists.</AlertBox>
            <div style={card()}>
              <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>Column Groups for AI Coding</span>
                <button style={btn('#f1f5f9', '#475569', { fontSize: 12, padding: '6px 12px' })} onClick={() => handleAddNewCodeGroup(`AI Group ${editableCodeGroups.length + 1}`)}>
                  <Plus size={12} /> Add Group
                </button>
              </div>
              <div style={{ padding: 16 }}>
                {editableCodeGroups.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13 }}>
                    No groups configured. All columns will share one codelist.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>Column</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>Assigned Group</th>
                    </tr></thead>
                    <tbody>
                      {selectedColumns.map(column => (
                        <tr key={column} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 12px' }}><Chip label={column} color="primary" /></td>
                          <td style={{ padding: '8px 12px' }}>
                            <select style={inp({ padding: '6px 10px', width: 'auto', minWidth: 200 })} value={columnCodelistMapping[column] ?? ''} onChange={e => { if (e.target.value === '') handleRemoveColumnFromGroup(column); else handleAssignColumnToGroup(column, parseInt(e.target.value)); }}>
                              <option value="">Not assigned (uses first group)</option>
                              {editableCodeGroups.map((group, idx) => <option key={idx} value={idx}>{group.header}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {editableCodeGroups.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {editableCodeGroups.map((group, idx) => {
                  const assignedCols = getColumnsForGroup(idx);
                  return (
                    <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <input style={inp({ width: 180, padding: '6px 10px' })} value={group.header} onChange={e => handleRenameCodeGroup(idx, e.target.value)} />
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{assignedCols.length > 0 ? assignedCols.join(', ') : 'No columns assigned'}</span>
                        {editableCodeGroups.length > 1 && <button onClick={() => handleDeleteCodeGroup(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f43f5e', padding: 4 }}><Trash2 size={13} /></button>}
                      </div>
                      <input style={inp({ padding: '6px 10px' })} placeholder="Column description (helps AI match accurately)" value={group.description || ''} onChange={e => handleUpdateGroupDescription(idx, e.target.value)} />
                    </div>
                  );
                })}
              </div>
            )}
            <AlertBox type="success">Configuration complete. Click "Next" to add custom instructions or proceed to review.</AlertBox>
          </div>
        );

        if (editableCodeGroups.length === 0 && !codelistFile) return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <AlertBox type="info">No codelist file uploaded. Go back to upload one, or use AI-generated codes.</AlertBox>
            <button style={btn('transparent', '#475569')} onClick={() => { setUseAIGenerated(true); setActiveStep(prev => prev + 1); }}>Use AI-generated codes instead</button>
          </div>
        );

        if (editableCodeGroups.length === 0 && codelistFile) return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <AlertBox type="warning">No code groups detected from the uploaded file. The format may not match expectations.</AlertBox>
            <button style={btn('#0f172a', '#fff')} onClick={() => handleAddNewCodeGroup('Code Group 1')}><Plus size={13} /> Create Codelist Manually</button>
            <button style={btn('transparent', '#475569')} onClick={() => { setUseAIGenerated(true); setActiveStep(prev => prev + 1); }}>Or let AI generate codes instead</button>
          </div>
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Column mapping section */}
            <div style={card()}>
              <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>Assign Columns to Code Groups</span>
                <button style={btn('#f1f5f9', '#475569', { fontSize: 12, padding: '6px 12px' })} onClick={() => handleAddNewCodeGroup(`Code Group ${editableCodeGroups.length + 1}`)}><Plus size={12} /> Add Group</button>
              </div>
              <div style={{ padding: 16 }}>
                <AlertBox type="info">
                  <strong>Format A:</strong> Multiple sheets (one codelist per sheet) — each sheet named after the column.<br />
                  <strong>Format B:</strong> Single sheet, multiple codeframes side by side — column header | Code, separated by empty column.
                </AlertBox>
                {editableCodeGroups.length > 1 && selectedColumns.length > 0 && (() => {
                  const matched = selectedColumns.filter(col => col in columnCodelistMapping);
                  const unmatched = selectedColumns.filter(col => !(col in columnCodelistMapping));
                  return (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {matched.length > 0 && <AlertBox type="success"><strong>{matched.length} column(s) auto-matched:</strong> {matched.join(', ')}</AlertBox>}
                      {unmatched.length > 0 && <AlertBox type="warning"><strong>{unmatched.length} column(s) have no matching codeframe:</strong> {unmatched.join(', ')}</AlertBox>}
                    </div>
                  );
                })()}
                <div style={{ overflowX: 'auto', marginTop: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>Column</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>Assigned Code Group</th>
                      <th style={{ width: 40 }} />
                    </tr></thead>
                    <tbody>
                      {selectedColumns.map(column => (
                        <tr key={column} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 12px' }}><Chip label={column} color="primary" /></td>
                          <td style={{ padding: '8px 12px' }}>
                            <select style={inp({ padding: '6px 10px', width: 'auto', minWidth: 200 })} value={columnCodelistMapping[column] ?? ''} onChange={e => { if (e.target.value === '') handleRemoveColumnFromGroup(column); else handleAssignColumnToGroup(column, parseInt(e.target.value)); }}>
                              <option value="">Not assigned</option>
                              {editableCodeGroups.map((group, idx) => <option key={idx} value={idx}>{group.header} ({group.codes.length} codes)</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <button onClick={() => handleColumnToggle(column)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 2 }}><Trash2 size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {getUnassignedColumns().length > 0 && <div style={{ marginTop: 8 }}><AlertBox type="info">{getUnassignedColumns().length} column(s) not yet assigned. They will use the first code group by default.</AlertBox></div>}
              </div>
            </div>

            {/* Group tabs */}
            <div style={card()}>
              <div style={{ display: 'flex', overflow: 'auto', borderBottom: '1px solid #e2e8f0' }}>
                {editableCodeGroups.map((group, idx) => (
                  <button key={idx} onClick={() => setSelectedGroupTab(idx)} style={{ padding: '10px 16px', border: 'none', borderBottom: `2px solid ${selectedGroupTab === idx ? '#0f172a' : 'transparent'}`, background: selectedGroupTab === idx ? '#f8fafc' : 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: selectedGroupTab === idx ? 700 : 400, color: selectedGroupTab === idx ? '#0f172a' : '#64748b', whiteSpace: 'nowrap' }}>
                    {group.header} ({group.codes.length}) [{getColumnsForGroup(idx).length} cols]
                  </button>
                ))}
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input style={inp({ width: 200, padding: '6px 10px' })} value={editableCodeGroups[selectedGroupTab]?.header || ''} onChange={e => handleRenameCodeGroup(selectedGroupTab, e.target.value)} placeholder="Group Name" />
                    {editableCodeGroups.length > 1 && <button onClick={() => handleDeleteCodeGroup(selectedGroupTab)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f43f5e', padding: 4 }}><Trash2 size={13} /></button>}
                  </div>
                  <button style={btn('#f1f5f9', '#475569', { fontSize: 12, padding: '6px 12px' })} onClick={() => handleAddCode(selectedGroupTab)}><Plus size={12} /> Add Code</button>
                </div>
                {getColumnsForGroup(selectedGroupTab).length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Columns using this codelist: </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {getColumnsForGroup(selectedGroupTab).map(col => <Chip key={col} label={col} color="primary" />)}
                    </div>
                  </div>
                )}
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 280, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead style={{ position: 'sticky', top: 0 }}>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {['Code', 'Label', 'Nets', 'Super Nets', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Actions' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {editableCodeGroups[selectedGroupTab]?.codes.map((code, codeIdx) => (
                        <tr key={codeIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '7px 12px', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>{code.code}</td>
                          <td style={{ padding: '7px 12px', color: '#334155' }}>{code.codelist}</td>
                          <td style={{ padding: '7px 12px', color: '#94a3b8' }}>{code.nets || '—'}</td>
                          <td style={{ padding: '7px 12px', color: '#94a3b8' }}>{code.super_nets || '—'}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                            <button onClick={() => handleEditCode(selectedGroupTab, codeIdx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', padding: 3 }}><Edit2 size={13} /></button>
                            <button onClick={() => handleDeleteCode(selectedGroupTab, codeIdx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f43f5e', padding: 3 }}><Trash2 size={13} /></button>
                          </td>
                        </tr>
                      ))}
                      {editableCodeGroups[selectedGroupTab]?.codes.length === 0 && (
                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: '28px 0', color: '#94a3b8', fontSize: 13 }}>No codes yet. Click "Add Code" to start.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 14 }}>
                  {codelistConfirmed ? (
                    <AlertBox type="success">Codelist confirmed! You can proceed to the next step.</AlertBox>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1 }}><AlertBox type="warning">Please review and confirm the codelist before proceeding.</AlertBox></div>
                      <button style={btn('#10b981', '#fff')} onClick={handleConfirmCodelist}><Check size={13} /> Confirm Codelist</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Reference Coded Data (Optional)</div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Paste example responses with their correct code labels. Format: <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>response text → "Label 1", "Label 2"</code></p>
            <textarea
              style={inp({ minHeight: 120, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' })}
              placeholder={`it is different from my mpesa payment method -> "Differentiation from competitors"\nit can track my business expenses and has AI features -> "Expense tracking", "AI-powered features"`}
              value={referenceText}
              onChange={e => handleReferenceTextChange(e.target.value)}
            />
            {parsedReferenceExamples.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <AlertBox type="success">
                  <strong>{parsedReferenceExamples.length} example{parsedReferenceExamples.length !== 1 ? 's' : ''} ready</strong>
                  {parsedReferenceExamples.slice(0, 3).map((ex, i) => (
                    <div key={i} style={{ fontSize: 12, marginTop: 4, color: '#065f46' }}>"{ex.text.length > 60 ? ex.text.substring(0, 60) + '…' : ex.text}" → {ex.labels.join(', ')}</div>
                  ))}
                  {parsedReferenceExamples.length > 3 && <div style={{ fontSize: 12, color: '#065f46' }}>…and {parsedReferenceExamples.length - 3} more</div>}
                </AlertBox>
              </div>
            )}
            {referenceText && parsedReferenceExamples.length === 0 && <div style={{ marginTop: 8 }}><AlertBox type="warning">No valid examples found. Use format: response text → "Label 1", "Label 2"</AlertBox></div>}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0' }} />

          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Custom Instructions (Optional)</div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>Add special instructions for the AI coding process.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={inp({ flex: 1 })} placeholder="e.g., Focus on brand names only" value={newInstruction} onChange={e => setNewInstruction(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddInstruction()} />
              <button style={btn('#0f172a', '#fff')} onClick={handleAddInstruction}>Add</button>
            </div>
            {customInstructions.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {customInstructions.map((inst, idx) => <Chip key={idx} label={inst} onDelete={() => handleRemoveInstruction(idx)} />)}
              </div>
            )}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0' }} />

          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Exclusion Criteria</div>
            {[{ key: 'code_98', label: 'Code 98 — No Information / N/A', textKey: 'code98KeywordsText', setter: setCode98KeywordsText, val: code98KeywordsText, configKey: 'code_98' },
              { key: 'code_99', label: 'Code 99 — Vague / Irrelevant', textKey: 'code99KeywordsText', setter: setCode99KeywordsText, val: code99KeywordsText, configKey: 'code_99' }].map(item => (
              <div key={item.key} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>{item.label}</div>
                <input style={inp()} placeholder="Keywords (comma separated)" value={item.val} onChange={e => item.setter(e.target.value)}
                  onBlur={e => setExclusionConfig(prev => ({ ...prev, [item.configKey]: { ...prev[item.configKey], keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k) } }))} />
              </div>
            ))}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0' }} />

          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Output Options</div>
            {[
              { label: 'Include Translation column in output (for non-English responses)', checked: enableTranslation, onChange: e => setEnableTranslation(e.target.checked), hint: 'When enabled, output Excel will include a Translation column next to each source column.', disabled: false },
              { label: 'Include Nets in output', checked: includeNets, onChange: e => { setIncludeNets(e.target.checked); if (!e.target.checked) setIncludeSuperNets(false); }, hint: 'When disabled, Nets and Super Nets columns are excluded from the output.', disabled: false },
              { label: 'Include Super Nets in output', checked: includeSuperNets, onChange: e => setIncludeSuperNets(e.target.checked), hint: 'When disabled, only Super Nets column is excluded; Nets are still generated.', disabled: !includeNets },
            ].map((opt, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: opt.disabled ? 'not-allowed' : 'pointer' }}>
                  <Toggle checked={opt.checked} onChange={opt.onChange} disabled={opt.disabled} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: opt.disabled ? '#94a3b8' : '#334155' }}>{opt.label}</span>
                </label>
                <div style={{ fontSize: 12, color: '#94a3b8', marginLeft: 50, marginTop: 3 }}>{opt.hint}</div>
              </div>
            ))}
            {useAIGenerated ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Toggle checked disabled />
                <span style={{ fontSize: 13, color: '#64748b' }}>Auto-create new codes is <strong>always on</strong> in AI coding mode.</span>
              </div>
            ) : (
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <Toggle checked={autoCreateCodes} onChange={e => setAutoCreateCodes(e.target.checked)} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Auto-create new codes for unmatched responses</span>
                </label>
                <div style={{ fontSize: 12, color: '#94a3b8', marginLeft: 50, marginTop: 3 }}>When enabled, AI will create new codelist entries for responses not matched by your uploaded codelist.</div>
                {autoCreateCodes && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, marginLeft: 50, fontSize: 13, color: '#475569' }}>
                    Only create new code if brand appears at least
                    <select style={inp({ padding: '5px 8px', width: 70 })} value={newCodeMinOccurrences} onChange={e => setNewCodeMinOccurrences(Number(e.target.value))}>
                      {[1,2,3,4,5,10].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    time{newCodeMinOccurrences !== 1 ? 's' : ''} (fewer → coded as Others)
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );

      case 5: return (
        <div style={card({ padding: 0 })}>
          <div style={sectionLabel}>Review Configuration</div>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Job Name', value: jobName },
              { label: 'Data File', value: `${dataFile?.name} (Sheet: ${selectedDataSheet})` },
              { label: 'Translation in Output', value: enableTranslation ? 'Enabled' : 'Disabled' },
              { label: 'Auto-create New Codes', value: useAIGenerated ? 'Always enabled (AI coding mode)' : autoCreateCodes ? `Enabled — min ${newCodeMinOccurrences} occurrence(s) to create` : 'Disabled (unmatched → Others)' },
            ].map(row => (
              <div key={row.label}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{row.label}</div>
                <div style={{ fontSize: 13.5, color: '#334155' }}>{row.value}</div>
              </div>
            ))}
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Columns to Code</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>{selectedColumns.map(col => <Chip key={col} label={col} color="primary" />)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Codelist</div>
              <div style={{ fontSize: 13.5, color: '#334155' }}>{useAIGenerated ? 'AI will generate codes' : `${codelistFile?.name || 'None'} (${codeGroups.length} code group${codeGroups.length !== 1 ? 's' : ''})`}</div>
            </div>
            {customInstructions.length > 0 && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Custom Instructions</div>
                {customInstructions.map((inst, idx) => <div key={idx} style={{ fontSize: 13, color: '#475569', marginBottom: 3 }}>• {inst}</div>)}
              </div>
            )}
          </div>
        </div>
      );

      default: return null;
    }
  };

  // ── Main render ──────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 28px', maxWidth: 820, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ background: '#0f172a', borderRadius: 12, padding: '18px 24px', marginBottom: 24 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>New Open End Coding Job</div>
        <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>Configure your AI-powered response coding job</div>
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {steps.map((label, idx) => {
          const isActive = idx === activeStep;
          const isDone = idx < activeStep;
          return (
            <div key={idx} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRight: idx < steps.length - 1 ? '1px solid #e2e8f0' : 'none', background: isActive ? '#f8fafc' : '#fff' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: isDone ? '#10b981' : isActive ? '#0f172a' : '#e2e8f0', color: isDone || isActive ? '#fff' : '#94a3b8', fontSize: 11, fontWeight: 700 }}>
                {isDone ? '✓' : idx + 1}
              </div>
              <span style={{ fontSize: 11.5, fontWeight: isActive ? 700 : 400, color: isActive ? '#0f172a' : isDone ? '#10b981' : '#94a3b8', lineHeight: 1.3 }}>{label}</span>
            </div>
          );
        })}
      </div>

      {/* Draft restore */}
      {pendingDraft && (
        <div style={{ marginBottom: 16 }}>
          <AlertBox type="info">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>You have a saved draft from {new Date(pendingDraft.savedAt).toLocaleString()} — step {pendingDraft.activeStep + 1} of {steps.length}.</span>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button style={btn('#0f172a', '#fff', { fontSize: 12, padding: '5px 12px' })} onClick={() => restoreDraft(pendingDraft)}>Resume</button>
                <button style={btn('transparent', '#64748b', { fontSize: 12, padding: '5px 12px' })} onClick={discardDraft}>Discard</button>
              </div>
            </div>
          </AlertBox>
        </div>
      )}

      {/* Error */}
      {error && <div style={{ marginBottom: 16 }}><AlertBox type="error" onClose={() => setError(null)}>{error}</AlertBox></div>}

      {/* Step content */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '24px', minHeight: 300, marginBottom: 20 }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 13 }}>Processing…</div>
        ) : renderStepContent()}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button style={btn('transparent', '#475569', { opacity: activeStep === 0 ? 0.4 : 1 })} onClick={handleBack} disabled={activeStep === 0}>← Back</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn('transparent', '#475569')} onClick={() => navigate('/oe-coding')}>Cancel</button>
          <button style={btn('#f1f5f9', '#475569')} onClick={saveDraft}><Save size={13} /> Save Progress</button>
          {activeStep === steps.length - 1 ? (
            <button style={btn('#10b981', '#fff', { opacity: !isStepValid() || isLoading ? 0.5 : 1 })} onClick={handleSubmit} disabled={!isStepValid() || isLoading}>
              <Check size={13} /> {isLoading ? 'Creating…' : 'Create Job'}
            </button>
          ) : (
            <button style={btn('#0f172a', '#fff', { opacity: !isStepValid() ? 0.4 : 1 })} onClick={handleNext} disabled={!isStepValid()}>Next →</button>
          )}
        </div>
      </div>

      {/* Draft saved toast */}
      {draftSaved && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#0f172a', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 9999 }}>
          Progress saved
        </div>
      )}

      {/* Edit Code Dialog */}
      <Modal open={editDialogOpen} onClose={() => setEditDialogOpen(false)}
        title={editingCode?.codeIndex === -1 ? 'Add New Code' : 'Edit Code'}
        subtitle={editingCode?.codeIndex === -1 ? 'Add a new code to this group' : 'Update code details'}
        actions={<>
          <button style={{ padding: '9px 18px', border: 'none', borderRadius: 9, background: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#64748b', fontFamily: 'inherit' }} onClick={() => setEditDialogOpen(false)}>Cancel</button>
          <button style={{ padding: '9px 20px', border: 'none', borderRadius: 9, background: !editCodeForm.code || !editCodeForm.codelist ? '#f1f5f9' : '#4f46e5', color: !editCodeForm.code || !editCodeForm.codelist ? '#94a3b8' : '#fff', cursor: !editCodeForm.code || !editCodeForm.codelist ? 'not-allowed' : 'pointer', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit' }} onClick={handleSaveCode} disabled={!editCodeForm.code || !editCodeForm.codelist}>Save</button>
        </>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { label: 'Code Number *', key: 'code', type: 'number', hint: '' },
            { label: 'Code Label *', key: 'codelist', type: 'text', hint: '' },
            { label: 'Nets (Optional)', key: 'nets', type: 'text', hint: 'Group codes under a net category' },
            { label: 'Super Nets (Optional)', key: 'super_nets', type: 'text', hint: 'Higher-level grouping' },
          ].map(f => (
            <div key={f.key}>
              <label style={labelCss}>{f.label}</label>
              <input style={inp()} type={f.type} value={editCodeForm[f.key]} onChange={e => setEditCodeForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
              {f.hint && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>{f.hint}</div>}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
